<template>
  <page-component
    id="webaccountmanagement"
    :header="`Household Account Management for the ${
      household.name || ''
    } Household`">
    <div id="webaccountmanagement-info">
      <div
        id="webaccountmanagement-hhdetails"
        class="group">
        <template v-if="!loading">
          <h2>Household Information</h2>
          <div class="webaccountmanagement-hhdetail">
            <strong>Household Number:</strong>
            <span>{{ household.number }}</span>
          </div>
          <template v-for="address in household.address">
            <div
              v-if="address"
              class="webaccountmanagement-hhdetail"
              :key="address">
              <span>{{ address }}</span>
            </div>
          </template>
          <div class="webaccountmanagement-hhdetail">
            <span
              >{{ household.city }}, {{ household.state }}
              {{ household.zip }}</span
            >
          </div>
        </template>
        <loading-component v-if="loading"></loading-component>
      </div>
      <div
        id="webaccountmanagement-manage"
        class="group">
        <h2>Manage Household and Members</h2>
        <p>
          Manage your Household Information for all linked members or Add or
          Update Members linked to this household.
        </p>
        <div>
          <a
            class="button"
            id="webaccountmanagement_buttonupdatehh"
            :href="
              baseHref + 'household.html?option=update&_csrf_token=' + crsfToken
            ">
            Household and Member Update
          </a>
        </div>
      </div>
    </div>
    <div
      id="webaccountmanagement-members"
      class="group">
      <template v-if="!loading">
        <table class="table">
          <caption class="sr-only">
            List of household members and their account details
          </caption>
          <thead>
            <tr>
              <th>User</th>
              <th>Last Login</th>
              <th>Status</th>
              <th>Permissions</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="member in members"
              :key="member.memberId"
              class="member-details">
              <td>
                <div class="member-info">
                  <span class="member-icon">
                    {{ memberIconText(member) }}
                  </span>
                  <span>
                    <div class="member-name">
                      {{ member.firstName }} {{ member.lastName }}
                    </div>
                    <div
                      v-if="member.emailAddress"
                      class="member-email">
                      <strong>Email:</strong>
                      <span v-if="member.isVerified">{{
                        member.emailAddress
                      }}</span>
                      <span v-if="!member.isVerified">
                        <a
                          href="#"
                          @click.prevent="onClickResendVerification(member)"
                          title="Resend Verification Email">
                          {{ member.emailAddress }}
                        </a>
                      </span>
                      <svgicon
                        v-if="member.isVerified"
                        original
                        class="checkbox__check"
                        data="@iconPath/glyph/16/check.svg?inline"
                        width="16"
                        height="16"
                        title="Email Verified">
                      </svgicon>
                      <svgicon
                        v-if="!member.isVerified"
                        original
                        class="checkbox__check"
                        data="@iconPath/glyph/16/info.svg?inline"
                        width="16"
                        height="16"
                        title="Email Unverified">
                      </svgicon>
                    </div>
                  </span>
                </div>
              </td>
              <td>
                <div class="member-lastactive">
                  <span>{{
                    $filters.formatDateTime(
                      member.lastActiveDateTime,
                      'DATETIME_MED'
                    )
                  }}</span>
                </div>
              </td>
              <td>
                <div class="member-status">
                  <span v-if="!member.isActive">Inactive</span>
                  <span v-else-if="member.isLockedOut">Locked Out</span>
                  <span
                    v-else-if="
                      member.isActive &&
                      (!member.hasAccount || !member.hasAccess) &&
                      member.hasInvite
                    "
                    :title="
                      'Invite Sent on ' +
                      $filters.formatDateTime(
                        member.inviteDateTime,
                        'DATETIME_MED'
                      ) +
                      '; Invite Expires on ' +
                      $filters.formatDateTime(
                        member.inviteExpires,
                        'DATETIME_MED'
                      )
                    "
                    >Invite Pending</span
                  >
                  <span
                    v-else-if="
                      member.isActive &&
                      (!member.hasAccount || !member.hasAccess)
                    "
                    >No Access</span
                  >
                  <span
                    v-else-if="
                      member.isActive && member.hasAccount && member.hasAccess
                    "
                    >User Active</span
                  >
                </div>
              </td>
              <td>
                <div
                  v-if="member.isActive"
                  :class="{
                    'member-permissions': true,
                    'member-permissions--state-toggle': permissions.length > 1,
                    'member-permissions--state-open':
                      permissionOpen[member.memberId]
                  }">
                  <div
                    v-for="permission in permissions"
                    :key="
                      member.memberId + '_' + normalizePermission(permission)
                    "
                    :class="[
                      'member-permission',
                      'member-permission-' + normalizePermission(permission)
                    ]">
                    <checkbox-component
                      :id="
                        'member_' +
                        member.memberId +
                        '_' +
                        normalizePermission(permission)
                      "
                      :label="permission"
                      :modelValue="member.permissions[permission]"
                      :disabled="member.isAccount || processing"
                      @change="onPermissionsChange(member, permission)" />
                  </div>
                  <div
                    v-if="permissions.length > 1"
                    class="member-permissions-toggle"
                    @click="onPermissionsClick(member.memberId)">
                    <svgicon
                      v-if="!permissionOpen[member.memberId]"
                      original
                      class="member-permissions-icon"
                      data="@iconPath/glyph/16/small-triangle-down-black.svg?inline"
                      width="16"
                      height="16">
                    </svgicon>
                    <svgicon
                      v-if="permissionOpen[member.memberId]"
                      original
                      class="member-permissions-icon"
                      data="@iconPath/glyph/16/small-triangle-up.svg?inline"
                      width="16"
                      height="16">
                    </svgicon>
                    <span>{{
                      permissionOpen[member.memberId]
                        ? 'Hide More'
                        : 'Show More'
                    }}</span>
                  </div>
                </div>
              </td>
              <td>
                <div
                  :id="'member-actions-' + member.memberId"
                  class="member-actions">
                  <button
                    v-if="!member.hasAccount || !member.hasAccess"
                    :id="'webaccountmanagement_buttoninvite_' + member.memberId"
                    class="button primary"
                    type="button"
                    :disabled="!member.isActive || processing"
                    :title="
                      !member.isActive
                        ? 'Unable to Invite an Inactive Member'
                        : ''
                    "
                    @click="onClickInvite(member)">
                    Invite
                  </button>
                  <button
                    v-if="member.hasAccount && member.hasAccess"
                    :id="'webaccountmanagement_buttonrevoke_' + member.memberId"
                    class="button primary"
                    type="button"
                    :disabled="member.isAccount || processing"
                    :title="
                      member.isAccount
                        ? 'Unable to Revoke Access from the logged in account'
                        : ''
                    "
                    @click="onClickRevokeAccess(member)">
                    Revoke Access
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
      <loading-component v-if="loading"></loading-component>
    </div>

    <dialog-component
      v-if="inviteDialogOpen"
      id="webaccountmanagement-dialog"
      :title="'Invite ' + inviteMember.firstName"
      :selector="'#member-actions-' + inviteMember.memberId"
      :hasButtonPane="true"
      @close="onClickCancelInvite()">
      <p>
        Invite {{ inviteMember.firstName }} to access the
        {{ household.name }} household?
      </p>

      <p>
        Select an existing email address on file or enter in a new email
        address.
      </p>

      <combobox-component
        id="invite"
        displayStyle="fillin"
        :items="inviteEmails"
        v-model="inviteEmail"
        :error="inviteEmailError"
        emptyLabel="No Email Addresses Found"
        @change="onInviteEmailChange()">
      </combobox-component>

      <div
        v-if="inviteEmailError"
        class="validation-error">
        <span class="validation-message">
          <span class="validation-message__text">
            {{
              inviteEmailErrorMessage !== ''
                ? inviteEmailErrorMessage
                : 'Invalid Email Address Entered!'
            }}</span
          >
        </span>
      </div>

      <div
        v-if="inviteEmailWarming"
        class="validation-warning">
        <span class="validation-message">
          <span class="validation-message__text"
            >This email address is already being used by another member of this
            household.</span
          >
        </span>
      </div>

      <p>
        <strong>Note:</strong> The email address entered here will become the
        primary email address for the invited member.
      </p>

      <template v-slot:buttons>
        <button
          id="webaccountmanagement-dialog_buttonsend"
          type="button"
          class="button primary"
          @click="onClickSendInvite()"
          :disabled="processing">
          Send Invite
        </button>
        <button
          id="webaccountmanagement-dialog_buttoncancel"
          type="button"
          class="button"
          @click="onClickCancelInvite()">
          Cancel
        </button>
      </template>
    </dialog-component>
  </page-component>
</template>

<script>
import AccountManagementServices from '@/services/AccountManagementServices'
import { mutations } from '@/store/types'
import { validEmail } from '@/core/util'

export default {
  name: 'AccountManagement',
  data() {
    return {
      loading: true,
      processing: false,
      permissionOpen: {},
      permissions: ['Account Management'],
      members: [],
      household: {},
      emailAddresses: [],
      baseHref: App.settings.BaseHref,
      inviteMember: {},
      inviteDialogOpen: false,
      inviteEmails: [],
      inviteEmail: [],
      inviteEmailError: false,
      inviteEmailWarming: false,
      inviteEmailErrorMessage: '',
      crsfToken: App.settings.CSRFToken
    }
  },
  created() {
    this.fetchMembers()
  },
  methods: {
    fetchMembers() {
      this.loading = true
      AccountManagementServices.getMembers()
        .then((response) => {
          this.loading = false
          this.household = Object.assign(
            {},
            this.household,
            response.household || {}
          )

          if (response.primaryEmail) {
            this.emailAddresses.push(response.primaryEmail)
          }

          this.members = (response.members || [])
            .map((member) => {
              const permissions = {}
              const memberPermissions = (member.permissions || '').split(',')

              this.permissionOpen[member.memberId] = false
              this.permissions.forEach((permission) => {
                permissions[permission] = memberPermissions.includes(permission)
              })

              if (!this.emailAddresses.includes(member.emailAddress)) {
                this.emailAddresses.push(member.emailAddress)
              }

              return Object.assign({}, member, {
                permissions
              })
            })
            // Sort the members be the order
            .sort((a, b) => {
              return a.order > b.order ? 1 : a.order < b.order ? -1 : 0
            })
        })
        .catch(() => {
          this.loading = false
        })
    },

    memberIconText(member) {
      if (member.firstName && member.lastName) {
        return member.firstName.substr(0, 1) + member.lastName.substr(0, 1)
      } else if (member.firstName && !member.lastName) {
        return member.firstName.substr(0, 2)
      } else if (!member.firstName && member.lastName) {
        return member.lastName.substr(0, 2)
      }
      return '&nbsp;'
    },

    normalizePermission(permission) {
      return permission.toLowerCase().replace(' ', '', 'gi')
    },

    onPermissionsClick(memberId, open) {
      if (typeof open === 'boolean' && this.permissionOpen[memberId] === open) {
        return
      }

      this.permissionOpen = Object.assign({}, this.permissionOpen, {
        [memberId]:
          typeof open === 'boolean' ? open : !this.permissionOpen[memberId]
      })
    },

    onPermissionsChange(member, permission) {
      if (!member || !member.memberId || !member.emailAddress) {
        return false
      }

      const permissionsObject = Object.assign({}, member.permissions, {
        [permission]: !member.permissions[permission]
      })

      // The permissions on the back-end needs to just be a comma delimited list of the permissions
      // the user has access to so just change them to an array with the permission code in it.
      const permissionsString = Object.keys(permissionsObject).filter(
        (key) => permissionsObject[key]
      )

      this.processing = true
      AccountManagementServices.changeMemberPermissions(
        member.memberId,
        permissionsString
      )
        .then((response) => {
          this.processing = false
          if (!response.success) {
            this.$store.commit(mutations.ADD_MESSAGE, {
              type: 'error',
              text:
                response.error ||
                'An error occurred while changing permissions. Please try again later.'
            })
            return
          }

          member.permissions = permissionsObject
        })
        .catch(() => {
          this.processing = false
          this.$store.commit(mutations.ADD_MESSAGE, {
            type: 'error',
            text: 'An error occurred while changing permissions. Please try again later.'
          })
        })
    },

    onClickInvite(member) {
      this.processing = true
      this.$store.commit(mutations.CLEAR_MESSAGES)

      AccountManagementServices.getEmailList(member.memberId)
        .then((emails) => {
          this.processing = false
          this.inviteDialogOpen = true
          this.inviteMember = member
          this.inviteEmail = []
          this.inviteEmailWarning = false
          this.inviteEmails = emails.map((email) => {
            return {
              label: email.emailAddress,
              value: email.emailAddress
            }
          })

          emails.forEach((email) => {
            if (email.isPrimary) {
              this.inviteEmail.push(email.emailAddress)
            }
          })
        })
        .catch(() => {
          this.processing = false
          this.inviteDialogOpen = false
        })
    },

    onClickSendInvite() {
      if (this.inviteEmail.length === 0) {
        this.inviteEmailError = true
        this.inviteEmailErrorMessage =
          'The email address is empty. Please enter a valid email address.'
      }

      if (this.inviteEmailError || !this.inviteMember) {
        return
      }

      this.$store.commit(mutations.CLEAR_MESSAGES)
      this.inviteEmailError = false
      this.inviteEmailErrorMessage = ''

      const member = Object.assign({}, this.inviteMember, {
        emailAddress: this.inviteEmail[0]
      })

      this.processing = true
      AccountManagementServices.inviteMember(
        member.memberId,
        member.emailAddress
      )
        .then((response) => {
          this.processing = false
          if (!response.success) {
            this.$store.commit(mutations.ADD_MESSAGE, {
              type: 'error',
              text:
                response.error ||
                'An error occurred while sending the invite. Please try again later.'
            })
            return
          }

          this.$store.commit(mutations.ADD_MESSAGE, {
            type: 'success',
            text: `Invite successfully sent to ${member.emailAddress}`
          })

          this.onClickCancelInvite()
          this.fetchMembers()
        })
        .catch(() => {
          this.processing = false
          this.$store.commit(mutations.ADD_MESSAGE, {
            type: 'error',
            text: 'An error occurred while sending the invite. Please try again later.'
          })
        })
    },

    onClickCancelInvite() {
      this.inviteDialogOpen = false
      this.inviteMember = {}
      this.inviteEmail = []
      this.inviteEmailWarming = false
      this.inviteEmailError = false
      this.inviteEmailErrorMessage = ''
    },

    onClickRevokeAccess(member) {
      if (!member || !member.memberId) {
        return
      }

      AccountManagementServices.changeMemberStatus(member.memberId, false)
        .then((response) => {
          this.processing = false

          if (response.success) {
            this.$store.commit(mutations.ADD_MESSAGE, {
              type: 'success',
              text: `Successfully revoked access for ${member.firstName} ${member.lastName}`
            })

            this.fetchMembers()
            return
          }

          this.$store.commit(mutations.ADD_MESSAGE, {
            type: 'error',
            text:
              response.error ||
              `Unable to revoke access at this time, please try again later.`
          })
        })
        .catch(() => {
          this.processing = false
          this.$store.commit(mutations.ADD_MESSAGE, {
            type: 'error',
            text: `Unable to revoke access at this time, please try again later.`
          })
        })
    },

    onInviteEmailChange() {
      this.inviteEmailErrorMessage = ''
      const email = this.inviteEmail.length > 0 ? this.inviteEmail[0] : ''
      if (!email || !validEmail(email)) {
        this.inviteEmailError = true
      } else {
        this.inviteEmailError = false
      }

      if (
        email &&
        this.emailAddresses.includes(email) &&
        email !== this.inviteMember.emailAddress
      ) {
        this.inviteEmailWarming = true
      } else {
        this.inviteEmailWarming = false
      }
    },

    onClickResendVerification(member) {
      if (!member || !member.memberId) {
        return
      }

      AccountManagementServices.sendVerificationEmail(member.memberId)
        .then((success) => {
          this.processing = false

          if (success) {
            this.$store.commit(mutations.ADD_MESSAGE, {
              type: 'success',
              text: `Successfully sent verification email to ${member.emailAddress}`
            })
            return
          }

          this.$store.commit(mutations.ADD_MESSAGE, {
            type: 'error',
            text: `Unable to send verification email at this time, please try again later.`
          })
        })
        .catch(() => {
          this.processing = false
          this.$store.commit(mutations.ADD_MESSAGE, {
            type: 'error',
            text: `Unable to send verification email at this time, please try again later.`
          })
        })
    }
  }
}
</script>

<style lang="less" scoped>
@import '../../assets/css/abstract/variables.less';
@import '../../assets/css/abstract/mixins.less';

.member-details {
  .member-info {
    display: flex;
    flex-direction: row;
    align-items: center;

    .member-icon {
      width: 36px;
      height: 36px;
      display: block;
      border-radius: 50%;
      text-align: center;
      font-size: 18px;
      font-weight: bold;
      line-height: 35px;
      color: @secondary-text-color;
      background-color: @secondary-color;
      margin-right: 12px;
    }

    .member-name {
      font-size: 120%;
    }

    .member-email {
      font-size: 90%;
      display: flex;
      align-items: center;

      strong {
        margin-right: 6px;
      }

      a {
        .default-focus-indicator(2px, @primary-color);
        font-size: 100%;
      }

      svg {
        margin-left: 6px;
      }
    }
  }

  .member-lastactive {
    font-size: 90%;
  }

  .member-status {
    font-size: 90%;
  }

  .member-permissions {
    min-width: 150px;

    .member-permissions-toggle {
      position: absolute;
      bottom: 0;
      right: 0;
      left: 0;
      text-align: center;
      height: 40px;
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 70%,
        rgba(255, 255, 255, 1) 100%
      );
      cursor: pointer;
      line-height: 16px;
      display: flex;
      flex-direction: row;
      font-size: 75%;
      align-items: flex-end;
      justify-content: center;
    }

    &.member-permissions--state-toggle {
      padding-bottom: 16px;
      max-height: 40px;
      position: relative;
      overflow: hidden;
    }

    &.member-permissions--state-open {
      transition: all 0.5s ease-in-out;
      max-height: 250px; // Adjust this when adding more lines in the permissions

      .member-permissions-toggle {
        height: 16px;
      }
    }
  }

  .member-actions {
    .button {
      width: 100%;
    }
  }
}

#webaccountmanagement-info {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0;

  .group {
    padding: 12px;
    width: 100%;
    margin-right: 12px;
    margin-bottom: 0;

    h2 {
      font-size: 1.5em;
      font-weight: normal;
      padding: 0;
      margin: 0;
      margin-bottom: 8px;
    }

    .webaccountmanagement-hhdetail {
      padding: 2px 0;

      strong {
        margin-right: 12px;
      }
    }

    &:last-child {
      margin-right: 0;
    }
  }
}

@media @phone-down {
  #webaccountmanagement-info {
    flex-direction: column;
  }
}
</style>

<style lang="less">
@import '../../assets/css/abstract/mixins.less';

#webaccountmanagement .checkbox-wrap {
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;
  align-items: center;

  label {
    font-size: 90%;
    width: auto;
    margin-left: 8px;
  }

  &.checkbox-wrap--state-disabled {
    opacity: 0.75;

    .checkbox {
      cursor: default;
    }
  }

  button.checkbox {
    .default-focus-indicator(3px, @primary-color);
  }
}

#webaccountmanagement-dialog {
  .validation-error,
  .validation-warning {
    margin-top: 6px;
  }

  .button {
    width: auto;
  }
}

@media @phone-down {
  #webaccountmanagement-dialog .ui-dialog-buttonset button {
    width: 100%;
  }
}
</style>
